import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Map } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  useWalkthrough,
  isFirstVisit,
  isTourCompleted,
  resetWalkthroughProgress,
} from "./useWalkthrough";
import { getAllTours } from "./tours";
import { useTheme } from "../../../context/ThemeContext";

/**
 * Inline info-icon button placed next to the page title.
 * Opens a dropdown listing available guided tours.
 * On first visit, auto-shows a welcome prompt.
 */
function WalkthroughMenu() {
  const { t } = useTranslation();
  const { theme, setThemeTo } = useTheme();
  const { startTour } = useWalkthrough(setThemeTo);
  const [open, setOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [pendingTour, setPendingTour] = useState(null);
  const menuRef = useRef(null);

  const tours = useMemo(() => getAllTours({ t }), [t]);

  // First-visit auto-prompt (once per mount)
  useEffect(() => {
    if (!isFirstVisit()) return;
    const timer = setTimeout(() => setShowWelcome(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Close menu on outside click
  useEffect(() => {
    if (!open && !showWelcome) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
        setShowWelcome(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, showWelcome]);

  const handleStartTour = useCallback(
    (tour) => {
      setOpen(false);
      setShowWelcome(false);
      if (theme === "dark") {
        setPendingTour(tour);
      } else {
        setTimeout(() => startTour(tour), 150);
      }
    },
    [startTour, theme],
  );

  const handleConfirmThemeSwitch = useCallback(() => {
    const tour = pendingTour;
    setPendingTour(null);
    setTimeout(() => startTour(tour), 150);
  }, [pendingTour, startTour]);

  const handleSkipThemeSwitch = useCallback(() => {
    const tour = pendingTour;
    setPendingTour(null);
    setTimeout(() => startTour(tour, { skipThemeSwitch: true }), 150);
  }, [pendingTour, startTour]);

  const handleStartAll = useCallback(() => {
    if (tours.length > 0) handleStartTour(tours[0]);
  }, [tours, handleStartTour]);

  const handleDismissWelcome = useCallback(() => {
    setShowWelcome(false);
  }, []);

  const handleResetProgress = useCallback(() => {
    resetWalkthroughProgress();
    setOpen(false);
  }, []);

  return (
    <div ref={menuRef} className="relative inline-flex items-center">
      {/* Welcome prompt (first visit) */}
      {showWelcome && (
        <div className="absolute top-full right-0 mt-2 w-72 rounded-xl border border-(--color-primary)/50 bg-(--color-bg) p-4 shadow-[0_0_24px_var(--color-shadow-primary)] z-9999">
          <p className="text-sm font-semibold text-(--color-text-primary) mb-1">
            {t("Welcome to Topology Builder!", {
              defaultValue: "Welcome to Topology Builder!",
            })}
          </p>
          <p className="text-xs text-(--color-text-muted) mb-3">
            {t(
              "Want a quick guided tour? We'll show you how to build topologies with drag-and-drop and AI assistance.",
              {
                defaultValue:
                  "Want a quick guided tour? We'll show you how to build topologies with drag-and-drop and AI assistance.",
              },
            )}
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleStartAll}
              className="flex-1 rounded-lg bg-(--color-primary) px-3 py-1.5 text-xs font-medium text-(--color-text-on-primary) hover:brightness-110 transition"
            >
              {t("Start Tour", { defaultValue: "Start Tour" })}
            </button>
            <button
              onClick={handleDismissWelcome}
              className="flex-1 rounded-lg border border-(--color-border) px-3 py-1.5 text-xs font-medium text-(--color-text-muted) hover:bg-(--color-primary)/10 transition"
            >
              {t("Maybe Later", { defaultValue: "Maybe Later" })}
            </button>
          </div>
        </div>
      )}

      {/* Theme switch confirmation */}
      {pendingTour && (
        <div className="absolute top-full right-0 mt-2 w-72 rounded-xl border border-(--color-primary)/50 bg-(--color-bg) p-4 shadow-[0_0_24px_var(--color-shadow-primary)] z-9999">
          <p className="text-sm font-semibold text-(--color-text-primary) mb-1">
            {t("Switch to light mode?", {
              defaultValue: "Switch to light mode?",
            })}
          </p>
          <p className="text-xs text-(--color-text-muted) mb-3">
            {t(
              "The walkthrough is easier to follow in light mode. We'll switch back when it's done.",
              {
                defaultValue:
                  "The walkthrough is easier to follow in light mode. We'll switch back when it's done.",
              },
            )}
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleConfirmThemeSwitch}
              className="flex-1 rounded-lg bg-(--color-primary) px-3 py-1.5 text-xs font-medium text-(--color-text-on-primary) hover:brightness-110 transition"
            >
              {t("Switch & Start", { defaultValue: "Switch & Start" })}
            </button>
            <button
              onClick={handleSkipThemeSwitch}
              className="flex-1 rounded-lg border border-(--color-border) px-3 py-1.5 text-xs font-medium text-(--color-text-primary) hover:bg-(--color-primary)/10 transition"
            >
              {t("Keep Dark", { defaultValue: "Keep Dark" })}
            </button>
          </div>
        </div>
      )}

      {/* Tour list dropdown */}
      {open && (
        <div className="absolute top-full right-0 mt-2 w-64 rounded-xl border border-(--color-primary)/30 bg-(--color-bg) shadow-[0_0_20px_var(--color-shadow-primary)] overflow-hidden z-9999">
          <div className="px-4 py-3 border-b border-(--color-border)">
            <p className="text-sm font-semibold text-(--color-text-primary)">
              {t("Guided Tours", { defaultValue: "Guided Tours" })}
            </p>
          </div>
          <ul className="py-1">
            {tours.map((tour) => {
              const completed = isTourCompleted(tour.id);
              return (
                <li key={tour.id}>
                  <button
                    onClick={() => handleStartTour(tour)}
                    className="w-full text-left px-4 py-2.5 hover:bg-(--color-primary)/10 transition-colors flex items-center gap-3"
                  >
                    <span
                      className={`shrink-0 w-5 h-5 rounded-full border flex items-center justify-center text-xs ${
                        completed
                          ? "border-green-500 bg-green-500/20 text-green-400"
                          : "border-(--color-primary)/40 text-(--color-text-muted)"
                      }`}
                    >
                      {completed ? "\u2713" : ""}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-(--color-text-primary) truncate">
                        {tour.label}
                      </p>
                      <p className="text-xs text-(--color-text-muted) truncate">
                        {tour.description}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="px-4 py-2 border-t border-(--color-border)">
            <button
              onClick={handleResetProgress}
              className="text-xs text-(--color-text-muted) hover:text-(--color-text-primary) transition-colors"
            >
              {t("Reset progress", { defaultValue: "Reset progress" })}
            </button>
          </div>
        </div>
      )}

      {/* Info icon button — sits inline next to page title */}
      <button
        onClick={() => {
          setShowWelcome(false);
          setOpen((prev) => !prev);
        }}
        className="relative w-10 h-10 rounded-2xl border border-(--color-border) bg-(--color-bg-card) text-(--color-text-primary) hover:bg-(--color-bg-overlay) transition-all flex items-center justify-center shadow-sm"
        title={t("Guided Tours", { defaultValue: "Guided Tours" })}
      >
        <Map className="w-4.5 h-4.5 text-(--color-primary)" />
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500 ring-2 ring-(--color-bg)" />
      </button>
    </div>
  );
}

export default WalkthroughMenu;
